import { createContext, Dispatch, ReactNode, useEffect, useReducer } from 'react';
import { useLocation } from 'react-router';
import unionWith from 'lodash/unionWith';
import isEqual from 'lodash/isEqual';
import differenceWith from 'lodash/differenceWith';
import {
    isExcludeProductSelection,
    isIncludeProductSelection,
    ProductSelection,
    ProductSelectionAction,
    ProductSelectionActions
} from './ProductSelectionTypes';
import { usePreviousProps } from '../../hooks/usePreviousProps';
import { Location } from 'history';
import queryString from 'query-string';

export const ProductSelectionContext = createContext<[ProductSelection, Dispatch<ProductSelectionActions>]>([
    undefined,
    () => {}
]);

export function ProductSelectionProvider({ children }: { children: ReactNode }) {
    const location = useLocation();
    const previousLocation = usePreviousProps(location);

    const [state, dispatch] = useReducer(productSelectionReducer, undefined);

    useEffect(() => {
        /**
         * By default, we have to reset the ProductSelection when the page is changed.
         * But when we only paginate we want to keep the ProductSelection. (enables cross page selections)
         */
        if (shouldCloseNavigationAfterLocationChange(location, previousLocation)) {
            dispatch({ type: ProductSelectionAction.CLOSE });
        }

        /**
         * The action param is not used inside the useEffect,
         * but it has to be in the dependency array,
         * because changes to the action trigger the
         */
    }, [location, previousLocation]);

    return <ProductSelectionContext.Provider value={[state, dispatch]}>{children}</ProductSelectionContext.Provider>;
}

export function productSelectionReducer(state: ProductSelection, action: ProductSelectionActions): ProductSelection {
    switch (action.type) {
        case ProductSelectionAction.INIT_INCLUDE_SELECTION:
            return { included: [] };

        case ProductSelectionAction.INIT_EXCLUDE_SELECTION:
            return { excluded: [], totalCount: action.totalCount };

        case ProductSelectionAction.SELECT: {
            if (isIncludeProductSelection(state)) {
                return { ...state, included: unionWith(state.included, action.keys, isEqual) };
            } else if (isExcludeProductSelection(state)) {
                return { ...state, excluded: differenceWith(state.excluded, action.keys, isEqual) };
            } else {
                return { included: action.keys };
            }
        }

        case ProductSelectionAction.DESELECT:
            if (isIncludeProductSelection(state)) {
                return { ...state, included: differenceWith(state.included, action.keys, isEqual) };
            } else if (isExcludeProductSelection(state)) {
                const newExcludedItems = unionWith(state.excluded, action.keys, isEqual);
                if (state.totalCount <= newExcludedItems.length) {
                    return undefined;
                }
                return { ...state, excluded: newExcludedItems };
            } else {
                return state;
            }

        case ProductSelectionAction.CLOSE:
            return undefined;

        default:
            return state;
    }
}

type LocationSearchParams = {
    // these are the only types we are interested in here.
    // that does not mean that these are all parameter. there are actually plenty more.
    filter?: string;
    ageCategory?: string;
    groupBy?: string;
};

export const shouldCloseNavigationAfterLocationChange = (
    nextLocation: Location,
    previousLocation?: Location
): boolean => {
    /**
     * Combine all possibly relevant params
     */
    const previousSearch: LocationSearchParams = queryString.parse(previousLocation?.search ?? '');
    const nextSearch: LocationSearchParams = queryString.parse(nextLocation.search);

    const previous = {
        pathname: previousLocation?.pathname,
        filter: previousSearch.filter,
        ageCategory: previousSearch.ageCategory,
        groupBy: previousSearch.groupBy
    };
    const next = {
        pathname: nextLocation.pathname,
        filter: nextSearch.filter,
        ageCategory: nextSearch.ageCategory,
        groupBy: nextSearch.groupBy
    };

    /**
     * Compare both states
     */
    return !isEqual(previous, next);
};
